import { useToast } from '@chakra-ui/react';
import {
  createOrder as createOrderMutation,
  deleteAllCart,
  deleteCart,
} from '@diamond/sol-buyer-context';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface UseCartActionsArgs {
  token: string;
  notify?: (type: 'success' | 'error', message: string) => void;
}

export const useCartActions = ({ token, notify }: UseCartActionsArgs) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const notifyFn =
    notify ??
    ((type: 'success' | 'error', message: string) =>
      toast({ status: type, title: message }));

  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => deleteCart(token, id),
    onSuccess: async () => {
      notifyFn('success', 'Item keranjang berhasil dihapus');
      await queryClient.invalidateQueries({
        queryKey: ['Cart'],
        exact: false,
        refetchType: 'all',
      });
    },
    onError: (error: Error) => {
      notifyFn('error', error.message);
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => deleteAllCart(token),
    onSuccess: async () => {
      notifyFn('success', 'Semua isi keranjang berhasil dihapus');
      await queryClient.invalidateQueries({
        queryKey: ['Cart'],
        exact: false,
        refetchType: 'all',
      });
    },
    onError: (error: Error) => {
      notifyFn('error', error.message);
    },
  });

  const createOrderMutationHook = useMutation({
    mutationFn: (payload: {
      warehouse_id: string;
      products: Array<{ id: string; quantity: number; note: string | null }>;
      voucher_codes?: string[];
    }) => createOrderMutation(token, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['Cart'],
        exact: false,
        refetchType: 'all',
      });
    },
  });

  return {
    deleteItem: deleteItemMutation.mutateAsync,
    deleteAll: deleteAllMutation.mutateAsync,
    createOrder: createOrderMutationHook.mutateAsync,
    isDeleting: deleteItemMutation.isPending || deleteAllMutation.isPending,
    isCreatingOrder: createOrderMutationHook.isPending,
  };
};
